/*!
 * EGT shared section builders (plain UA/JSON helpers).
 * Every page element composes nav + content + footer under one `.egt` root,
 * so design tokens (scoped to .egt) apply and there is no nested-element
 * resolution dependency. No address/phone/email anywhere (founder rule).
 */

var mediaInventory = require("./egt.media-inventory.js");
var Capture = require("./egt.capture.js");

/* Re-exported so any surface can drop a capture in with S.capture({source:…}) —
 * the placements self-segment via the source→newsletter map in egt.capture.js
 * (STR-3: a free-chapter capture under every drop). */
exports.capture = Capture.capture;
exports.CAPTURE_SOURCES = Capture.SOURCES;

var ROOMS = [
  /* P2.3: "Universe" → "Platform". P2.2: this item is a DROPDOWN (menu:"platform")
   * listing the capability sub-pages (sourced from egt.platform.sections LENSES so
   * the menu never drifts from the platform page) + a Compare entry. The item's
   * href still points at /platform so cmd/middle-click and no-JS both reach the
   * page — but a PLAIN desktop click OPENS THE PANEL and does not navigate
   * (egt.nav.js; the panel's pinned first row is the in-panel route to
   * /platform). Founder-reported regression, see egt.nav.js's header. */
  { label: "Platform", href: "/platform", title: "The Universe Platform", menu: "platform" },
  { label: "Forge", href: "/forge", title: "EGT Forge — creative & marketing agency" },
  /* Entertainment is now a DROPDOWN (menu:"entertainment"), mirroring Platform:
   * a halfscreen panel with an "Explore Entertainment" pin, the book Exploit
   * (MOVED here from the old top-level Exploit room), and a per-album Music list
   * (built from ALBUMS). The item's href still points at /entertainment so
   * cmd/middle-click + no-JS both reach the overview page; a plain desktop click
   * OPENS THE PANEL (egt.nav.js). /exploit is gated dark until launch (see
   * app/routes/egt.pages.js GATED) — the submenu links to it just like the
   * footer's Exploit entry does; the route 404s until the founder flips the flag,
   * no special handling needed. */
  { label: "Entertainment", href: "/entertainment", title: "EGT — music, film, podcasts & the book Exploit", menu: "entertainment" },
  /* The "Voice" room retired 2026-07-29 — the Voice microsite was REPLACED by
   * the EGT Studio* launch surface. Same nav slot, new destination; /voice
   * 301-redirects to /studio (app/routes/egt.pages.js REDIRECTS). RELABELLED to
   * "AI Film Studio" (founder, 2026-07-31), then "Film Studio" (founder, 2026-10-24). `slug:"studio"` is REQUIRED: without
   * it navRoomSlug() would derive "aifilmstudio" from the label and break the
   * per-room mobile icon mapping (/imgs/nav/studio.svg) — the explicit slug keeps
   * the studio.svg icon resolving without renaming the asset.
   * NOTE: the old top-level Exploit + Intrepid rooms were REMOVED from the main
   * bar (2026-07-31) — Exploit now lives in the Entertainment submenu; Intrepid
   * remains reachable inside the Platform submenu (platformMenuItems()). */
  { label: "Film Studio", href: "/studio", title: "EGT* Film Studio", slug: "studio" },
  /* Company — a DROPDOWN (menu:"company") grouping the brand-level pages: the
   * Founder credibility page (/founder) and About (/about). Mirrors the
   * Platform/Entertainment dropdown pattern (companyDropdownInner). The item's
   * href points at /about so cmd/middle-click + no-JS reach a real page; a plain
   * desktop click opens the panel (egt.nav.js). Its mobile icon is
   * /imgs/nav/company.svg (added alongside this room) so the drill-in row's mask
   * resolves — an unmapped slug would paint a solid gold square. */
  { label: "About", href: "/about", title: "EGT — the company & the founder", menu: "company", slug: "company" }
];

/* Shared bootstrap-tooltip config (BS 5.3). The framework's RenderTooltips
 * (uat/src/interface/interface.js) does `new bootstrap.Tooltip(el)` with NO
 * options object, so every option has to arrive via data-bs-* attributes on the
 * trigger. We ROOT-CAUSE the flicker here: (1) trigger:"hover" only — no focus,
 * so a click/tab never leaves a stuck tooltip nor double-fires with hover;
 * (2) container:"body" appends the tooltip to <body>, escaping the nav's
 * backdrop-filter (which is a containing block and was making Popper reposition/
 * jitter the tooltip frame-by-frame); (3) a small show delay debounces the
 * pointer crossing the link's text box. The final flicker guard —
 * pointer-events:none so the tooltip can never intercept the pointer and bounce
 * the trigger's own hover on/off — lives on .egt-tip in overrides.scss. The
 * custom class also hangs the frosted-glass skin. */
function tooltip(node, placement) {
  node["data-toggle"] = "tooltip";
  node["data-bs-trigger"] = "hover";
  node["data-bs-placement"] = placement || "bottom";
  node["data-bs-container"] = "body";
  node["data-bs-delay"] = '{"show":90,"hide":0}';
  node["data-bs-custom-class"] = "egt-tip";
  return node;
}

/* Brand / accent palettes — the "Make it yours" selector. Each recolors the
 * scarce accent site-wide via the [data-egt-palette] blocks in _egt.scss
 * (they override --egt-gold / --egt-gold-rgb on the .egt token scope). The
 * true-black base + neutral text never change — only the accent. `hex` here
 * is the swatch preview only; the actual restyle is driven by the SCSS blocks
 * keyed off `id`. Default = gold (the base tokens, no SCSS block needed).
 * Keep this list in sync with the [data-egt-palette] blocks in _egt.scss. */
var PALETTES = [
  { id: "gold",   nm: "Gold",   hex: "#C48E2F" },
  { id: "bronze", nm: "Bronze", hex: "#B07A3A" },
  { id: "amber",  nm: "Amber",  hex: "#D9A227" },
  { id: "ice",    nm: "Ice",    hex: "#B9C4D0" },
  { id: "ember",  nm: "Ember",  hex: "#C2622F" }
];

/* Footer "The Studios" column. The Universe entry renders its full brand name
 * with the founder's emphasis: "Universe Platform" bright, "The"/"by" muted,
 * "EGT" in the accent. The rest carry a one-line byline under the link. */
var UNIVERSE_BRAND = [
  { n: "span", c: "egt-foot__bw-mute", i: "The " },
  { n: "span", c: "egt-foot__bw-hi", i: "Universe Platform" },
  { n: "span", c: "egt-foot__bw-mute", i: " by " },
  { n: "span", c: "egt-foot__bw-gold", i: "EGT" }
];
var STUDIOS = [
  { nm: UNIVERSE_BRAND, desc: "Unify everything.", href: "/platform" },
  { nm: "Intrepid", desc: "AI partner working while you sleep.", href: "/intrepid" },
  { nm: "Forge", desc: "Creative agency", href: "/forge" },
  { nm: "Film Studio", desc: "EGT* Film Studio", href: "/studio" },
  { nm: "Entertainment", desc: "Music, film & the book", href: "/entertainment" },
  { nm: "The Journey of the Coin", desc: "Short film", href: "/entertainment/the-journey-of-the-coin" },
  { nm: "Exploit", desc: "Book One of The Universe", href: "/exploit" }
];

/* The podcasts (spec 35). Pages are served from the content store
 * (app/routes/egt.content.pages.js); /classicman 301s to its page once live. */
var PODCASTS = [
  { nm: "Create_", href: "/podcasts/create", desc: "An EGT podcast",
    navDesc: "An EGT podcast about making things: brand, marketing and AI." },
  { nm: "Classic Man Podcast", href: "/podcasts/classic-man", desc: "Healthy, non-toxic masculinity",
    navDesc: "Healthy, non-toxic masculinity." }
];
exports.PODCASTS = PODCASTS;

/* The Making (/journal) is linked from the footer only once it has published
 * entries — never link to a 404. Flip with the launch (spec 35 WP3). */
var JOURNAL_LIVE = true;
exports.JOURNAL_LIVE = JOURNAL_LIVE;

/* NOTE: the nav accent-palette control (desktop popover + mobile swatch row)
 * was removed per WP-1.4. The underlying [data-egt-palette] theming blocks in
 * _egt.scss are intentionally KEPT (they still recolor the accent if a palette
 * is ever set), but the nav no longer surfaces a control to change it. The
 * PALETTES list below is retained as the source-of-truth doc for those blocks. */

/* P2.2 — the Platform dropdown's items, single-sourced from the platform page so
 * the nav menu never drifts from it. The six capabilities come from LENSES (reuse
 * label + href + one-line copy + live/badge); Compare / "The Stack" is appended as
 * the final entry. Lazy-required inside the function to avoid a load-time circular
 * dependency (egt.platform.sections.js requires THIS file at its top). */
function platformMenuItems() {
  var LENSES = require("./egt.platform.sections.js").LENSES;
  /* Pinned TOP item — the whole-Universe overview, then the six capabilities. */
  var items = [{
    label: "Explore the entire platform", href: "/platform", pinned: true,
    desc: "The whole platform, in one place — click through for the full picture."
  }];
  LENSES.forEach(function (l) {
    /* intentionally NO live / badge — locked decision: no live/coming tags in
     * this menu (the tags rendered nothing after a1c15a9 anyway). */
    items.push({ label: l.label, href: l.href, desc: l.copy });
  });
  /* Intrepid also gets its own top-level nav room (ROOMS, above), but the
   * founder wants it reachable from the Platform panel too. */
  items.push({ label: "Intrepid", href: "/intrepid",
    desc: "Your AI partner, working while you sleep." });
  items.push({ label: "Compare", href: "/platform/compare/the-stack",
    desc: "The whole stack, side by side — every capability in one view." });
  return items;
}


/* ── navV2 — the SAME nav, rebuilt on the shared ua.navbar framework
 * control (uat/src/elements/ua.navbar.js + ua.navbar.item.js +
 * ua.navbar.menu.js) instead of hand-rolled markup. Founder directive:
 * stop drifting from the framework's own nav component. ua.navbar's
 * "minimal" type + showExpandedMenu auto-generates BOTH the desktop bar
 * AND a mobile menu from the SAME `items` list (no separate mobileMenu()
 * needed — one source of truth, which also fixes "not mobile ready").
 * The Platform capability list becomes a halfscreen dropdown menu
 * (hover on desktop, tap on mobile) via ua.navbar.item's `menu` field —
 * ua.navbar.menu.js:OpenWithCurrentPath makes it genuinely context-aware
 * (auto-drills to the submenu containing the current page on open).
 * Visual skin lives in _egt.scss under .ua-navbar (new rules), matching
 * the frosted-glass gold/black brand — ua.navbar emits its own class
 * names (.ua-navbar, .nav-holster, .ua-navbar-item, .ua-navbar-menu),
 * not .egt-nav__*, so this is a parallel skin, not a reuse of the old one. */
function platformDropdownInner() {
  var all = platformMenuItems();
  var pinned = all.filter(function (it) { return it.pinned; });
  var rest = all.filter(function (it) { return !it.pinned; });
  function row(it, cls) {
    return { n: "a", href: it.href, c: "egt-navv2__pl-item" + (cls ? " " + cls : ""),
      i: [ { n: "span", c: "egt-navv2__pl-lb", i: it.label },
        { n: "span", c: "egt-navv2__pl-desc", i: it.desc } ] };
  }
  return { n: "div", c: "egt-navv2__pl",
    i: [
      { n: "div", c: "egt-navv2__pl-pinned", i: pinned.map(function (it) { return row(it, "egt-navv2__pl-item--pin"); }) },
      /* subheader over the capability list so it reads as "a taste of the
       * features" — the pinned link above is the way to the full platform page. */
      { n: "p", c: "egt-navv2__pl-subhead", i: "Discover Platform Features" },
      { n: "div", c: "egt-navv2__pl-list", i: rest.map(function (it) { return row(it); }) }
    ] };
}

/* ── the Entertainment HALFSCREEN dropdown — same frosted skin + row builder as
 * Platform (egt-navv2__pl* classes), so no parallel skin. Structure mirrors the
 * platform panel's pinned + subhead + list regions:
 *   - pinned:  "Explore Entertainment" → /entertainment (the overview page)
 *   - "The Book" subhead → Exploit → /exploit (MOVED here from the old top-level
 *     Exploit room)
 *   - "Music" subhead → one row per ALBUM (label = album name, href =
 *     /entertainment/<id>), in ALBUMS array order. The list carries the
 *     `--ent` marker class so the DESKTOP panel can render it as a tidy
 *     multi-column grid (the `menu.class` option is dropped by the framework for
 *     halfscreen/mobile menus — see _egt.scss — so the styling hook has to live
 *     inside `inner`, not on the menu element). */
function entertainmentDropdownInner() {
  function row(it, cls) {
    var inner = [ { n: "span", c: "egt-navv2__pl-lb", i: it.label } ];
    if (it.desc) { inner.push({ n: "span", c: "egt-navv2__pl-desc", i: it.desc }); }
    return { n: "a", href: it.href, c: "egt-navv2__pl-item" + (cls ? " " + cls : ""), i: inner };
  }
  /* A row that opens the next layer (founder 2026-10-12: Entertainment lists Film,
   * Music, Podcasts and Journal; each opens its own layer). ua.navbar.item with a
   * `menu` is the framework's own nesting: the row becomes a menu trigger and the
   * layer gets the back button / breadcrumb, on desktop and in the phone drawer. */
  function layer(label, desc, title, items, cols) {
    return { namespace: "ua.navbar.item", src: items.overview || "#", title: label, "class": "egt-navv2__pl-layer-lk",
      menu: { type: "halfscreen", class: "egt-navv2__menu egt-navv2__menu--drill", inner: { n: "div", c: "egt-navv2__pl egt-navv2__pl--ent egt-navv2__pl--layer", i: [
        { n: "div", c: "egt-navv2__pl-pinned", i: [ row({ label: title, href: items.overview, desc: desc }, "egt-navv2__pl-item--pin") ] },
        { n: "div", c: "egt-navv2__pl-list" + (cols ? " egt-navv2__pl-list--cols" : ""), i: items.rows }
      ] } } };
  }
  var albums = ALBUMS.map(function (a) { return row({ label: a.nm, href: "/entertainment/" + a.id }); });
  var layers = [
    layer("Film", "Short films from the Film Studio.", "Film", { overview: "/entertainment#film", rows: [
      row({ label: "The Journey of the Coin", href: "/entertainment/the-journey-of-the-coin", desc: "Short film \u2014 watch it, then read how it was made." }),
      row({ label: "Film Studio", href: "/studio", desc: "The studio behind the films." }) ] }),
    layer("Music", "Every album, in one place.", "All music", { overview: "/entertainment#wall", rows: albums }, true),
    /* Podcasts (spec 35 WP1, handoff D036): "podcasts", never "shows" (D034). */
    layer("Podcasts", "Long-form conversations from EGT.", "All podcasts", { overview: "/podcasts",
      rows: PODCASTS.map(function (p) { return row({ label: p.nm, href: p.href, desc: p.navDesc }); }) })
  ];
  if (JOURNAL_LIVE) {
    layers.push(layer("Journal", "The founder's journal on building EGT.", "The Making", { overview: "/journal", rows: [
      row({ label: "Latest entries", href: "/journal", desc: "The newest entries, first." }),
      row({ label: "Discover", href: "/journal/discover", desc: "Search every entry by subject or series." }) ] }));
  }
  return { n: "div", c: "egt-navv2__pl egt-navv2__pl--ent",
    i: [
      { n: "div", c: "egt-navv2__pl-pinned",
        i: [ row({ label: "Explore Entertainment", href: "/entertainment",
          desc: "Film, music, podcasts" + (JOURNAL_LIVE ? ", the journal" : "") + " & the book, all in one place." }, "egt-navv2__pl-item--pin") ] },
      { n: "div", c: "egt-navv2__pl-layers", i: layers },
      { n: "p", c: "egt-navv2__pl-subhead", i: "The Book" },
      { n: "div", c: "egt-navv2__pl-list",
        i: [ row({ label: "Exploit", href: "/exploit", desc: "Book One of The Universe." }) ] }
    ] };
}

/* ── the Company HALFSCREEN dropdown — same frosted skin + row builder as
 * Platform/Entertainment (egt-navv2__pl* classes). Structure mirrors the
 * entertainment panel: a pinned overview link, then the brand-level pages.
 *   - pinned:  "About EGT" → /about (who we are, in full)
 *   - "The Founder" subhead → Justin K Kazmierczak → /founder (the credibility
 *     page: who he is, what EGT is building, how to reach him). */
function companyDropdownInner() {
  function row(it, cls) {
    var inner = [ { n: "span", c: "egt-navv2__pl-lb", i: it.label } ];
    if (it.desc) { inner.push({ n: "span", c: "egt-navv2__pl-desc", i: it.desc }); }
    return { n: "a", href: it.href, c: "egt-navv2__pl-item" + (cls ? " " + cls : ""), i: inner };
  }
  return { n: "div", c: "egt-navv2__pl egt-navv2__pl--company",
    i: [
      { n: "div", c: "egt-navv2__pl-pinned",
        i: [ row({ label: "About EGT", href: "/about",
          desc: "A house of studios — who we are and what we're building." }, "egt-navv2__pl-item--pin") ] },
      { n: "p", c: "egt-navv2__pl-subhead", i: "The Founder" },
      { n: "div", c: "egt-navv2__pl-list",
        i: [ row({ label: "Justin K Kazmierczak", href: "/founder",
          desc: "The founder — and the case that EGT is the real thing." }) ]
          .concat(JOURNAL_LIVE ? [ row({ label: "The Making", href: "/journal", desc: "The founder's journal on building EGT." }) ] : []) }
    ] };
}

/* room label → a stable slug for the per-room mobile-menu icon class
 * (.egt-navv2__m-lk--<slug>); the icon itself is a gold-masked inline SVG
 * attached in _egt.scss (/imgs/nav/<slug>.svg). An explicit `slug` on the ROOMS
 * entry WINS over the derived label so a display-name change (e.g. "Studio" →
 * "AI Film Studio") cannot break the icon mapping. */
function navRoomSlug(r) {
  if (r.slug) { return r.slug; }
  return (r.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function navV2(reserveUrl, opts) {
  reserveUrl = reserveUrl || "/reserve";
  opts = opts || {};

  /* MINIMAL, LOCKED nav (opt-in via opts.minimalNav) — a conversion-first paid
   * landing strips every competing route so only the brand lockup and a single
   * CTA remain. When set, the room links and the mobile burger are omitted
   * entirely; the CTA below is the only interactive control. Default (unset) is
   * the full site chrome, so no other surface is affected. */
  var minimal = opts.minimalNav === true;

  /* Desktop bar — the 6 rooms; Platform carries the halfscreen hover dropdown. */
  var items = minimal ? [] : ROOMS.map(function (r) {
    var item = {
      namespace: "ua.navbar.item",
      src: r.href,
      title: r.title || r.label,
      quickName: r.label,
      hideText: false
    };
    /* ua-navbar-item-menu-halfscreen-hover is kept as the NO-JS fallback (the
     * framework still hover-opens the panel if egt.nav.js never runs); egt.nav.js
     * strips it on boot and takes the trigger click-only. egt-navv2__room--hasmenu
     * is the SHARED caret/aria-expanded styling hook (both dropdown rooms); the
     * per-room class (--platform / --entertainment) is what egt.nav.js targets so
     * each trigger owns its own panel independently. */
    if (r.menu === "platform") {
      item.navBarClass = "ua-navbar-item-menu-halfscreen-hover egt-navv2__room--hasmenu egt-navv2__room--platform";
      item.menu = { type: "halfscreen", class: "egt-navv2__menu", inner: platformDropdownInner() };
    } else if (r.menu === "entertainment") {
      item.navBarClass = "ua-navbar-item-menu-halfscreen-hover egt-navv2__room--hasmenu egt-navv2__room--entertainment";
      item.menu = { type: "halfscreen", class: "egt-navv2__menu", inner: entertainmentDropdownInner() };
    } else if (r.menu === "company") {
      item.navBarClass = "ua-navbar-item-menu-halfscreen-hover egt-navv2__room--hasmenu egt-navv2__room--company";
      item.menu = { type: "halfscreen", class: "egt-navv2__menu", inner: companyDropdownInner() };
    }
    return item;
  });

  /* Mobile menu — the SAME rooms with SHORT labels + a per-room icon class,
   * and Platform keeps its capability list as a drill-in submenu (founder:
   * the mobile menu must support submenus too). ua.navbar.menu renders each
   * as .ua-navbar-item > a (NOT .nav-link) — styled in _egt.scss. */
  var mobileItems = ROOMS.map(function (r) {
    var item = {
      namespace: "ua.navbar.item",
      src: r.href,
      title: r.label,
      "class": "egt-navv2__m-lk egt-navv2__m-lk--" + navRoomSlug(r)
    };
    if (r.menu === "platform") {
      item.menu = { type: "halfscreen", class: "egt-navv2__menu egt-navv2__menu--drill", inner: platformDropdownInner() };
    } else if (r.menu === "entertainment") {
      item.menu = { type: "halfscreen", class: "egt-navv2__menu egt-navv2__menu--drill", inner: entertainmentDropdownInner() };
    } else if (r.menu === "company") {
      item.menu = { type: "halfscreen", class: "egt-navv2__menu egt-navv2__menu--drill", inner: companyDropdownInner() };
    }
    return item;
  });
  /* N1: mobile-only Home affordance. On a phone the 6 rooms carry
   * d-none d-md-flex (shared ua.navbar) so the only route home is the brand
   * glyph — which the full-screen burger drawer occludes when open. Prepend
   * ONE Home entry to the DRAWER list only; the desktop `items` list above is
   * left untouched so the desktop lockup is undisturbed. */
  mobileItems.unshift({
    namespace: "ua.navbar.item",
    src: "/",
    title: "Home",
    "class": "egt-navv2__m-lk egt-navv2__m-lk--home"
  });

  /* Nav CTA is OPT-IN (founder ruling, 2026-08-08). The default "Join the Beta"
   * CTA belongs ONLY to the platform family; every other page shows its OWN CTA
   * or none. So a page renders a nav CTA only when it either (a) passes its own
   * opts.navCta, or (b) opts into the platform-family beta via opts.betaCta:true.
   * A page that does NEITHER shows NO nav CTA — which means a NEW page can never
   * accidentally pitch the beta. opts.hideNavCta still forces it off regardless. */
  var custom = !!opts.navCta;
  var ctaLabel = custom ? opts.navCta.label : "Join the Beta";
  var ctaHref = custom ? opts.navCta.href : reserveUrl;
  var showCta = !opts.hideNavCta && (custom || opts.betaCta === true);

  /* phone: the page CTA moves into the drawer as its first entry */
  if (showCta && !minimal) {
    mobileItems.splice(0, 0, { namespace: "ua.navbar.item", src: ctaHref, title: ctaLabel,
      "class": "egt-navv2__m-lk egt-navv2__m-cta" });
  }

  var last = [];
  if (showCta) {
    last.push({
      namespace: "ua.navbar.item",
      src: ctaHref,
      title: ctaLabel,
      navBarClass: "egt-navv2__cta",
      /* opts.navCta.style "link" renders the CTA as a quiet gold text link
       * instead of the pill (editorial pages — the film article, 2026-10-05). */
      navItemClass: "egt-navv2__cta-item" + (custom && opts.navCta.style === "link" ? " egt-navv2__cta-item--link" : "")
    });
  }

  /* Login + Contact (founder 2026-10-24): two icon-only links pinned with the
   * burger at the bar's right edge on EVERY page (not the minimal paid-landing
   * nav). Order on the bar: [page CTA, if any] [login] [contact] [menu] — the CTA
   * takes whatever room is left of the fixed group. Icons are CSS-masked SVGs
   * (bootstrap-icons isn't loaded site-wide); the anchors carry an aria-label
   * via the item title. On a phone the page CTA leaves the bar (_egt.scss) and
   * shows as the drawer's first entry instead (mobileItems above). */
  if (!minimal) {
    last.push({ namespace: "ua.navbar.item", src: "https://dashboard.egt.studio", title: "Log in", type: "icon-only",
      navBarClass: "egt-navv2__icon egt-navv2__icon--login", navItemClass: "egt-navv2__icon-item" });
    last.push({ namespace: "ua.navbar.item", src: "/contact-us", title: "Contact us", type: "icon-only",
      navBarClass: "egt-navv2__icon egt-navv2__icon--contact", navItemClass: "egt-navv2__icon-item" });
  }

  /* Our OWN burger (showExpandedMenu:false) instead of the framework's auto
   * one — lets us (a) draw a real hamburger in CSS (bootstrap-icons isn't
   * loaded site-wide, so `bi` glyphs render as tofu), (b) feed a curated
   * mobileItems list, and (c) add `ua-navbar-menu-open-current` so
   * ua.navbar.menu.js drills straight to the submenu holding the current
   * page (the founder's "context-aware" ask). d-lg-none: shown only on the
   * mobile breakpoint. */
  if (!minimal) {
    last.push({
      namespace: "ua.navbar.item",
      src: "#",
      title: "Menu",
      type: "icon-only",
      navBarClass: "d-lg-none egt-navv2__burger ua-navbar-menu-open-current",
      menu: { type: "mobile", "class": "egt-navv2__mobile", items: mobileItems }
    });
  }

  return {
    n: "ua.navbar",
    id: "egt-nav",
    type: "minimal",
    /* ua-navbar-sticky — NOT for stickiness (sticky:true already handles
     * that via position-sticky) but because ua.navbar.menu.js's
     * adjustHalfMenuPosition() specifically looks for this class to set
     * the halfscreen dropdown's top offset to sit right below the bar;
     * without it the dropdown defaults to top:0 and overlaps the nav. */
    "class": "egt-navv2 ua-navbar-sticky",
    sticky: true,
    brand: true,
    /* F41 — the visible "EGT" wordmark beside the mark, for TRADEMARK
     * use-in-commerce. navV2() has a single caller (S.page()), so this one
     * node puts it on every surface: visible, consistent, every page, which
     * is exactly what a use-in-commerce claim wants. This REVERSES the
     * Round-2 brand fact "logo = SVG + Lottie, no wordmark"
     * (task-tracker/00-DECISIONS-LOG.md) — amended there the same day so a
     * later agent does not strip this back off as off-brand.
     * A11Y: the img keeps a DESCRIPTIVE alt ("Coin Logo" — it describes the
     * mark) while the visible text carries the brand name. alt="EGT" would
     * make a screen reader announce "EGT EGT"; alt="" would lose the mark's
     * description entirely. Founder resolved this 2026-07-24. */
    brandInner: { n: "span", c: "egt-navv2__brand", i: [
      { n: "img", c: "egt-navv2__logo", src: "/logo.svg", alt: "Coin Logo" },
      { n: "span", c: "egt-navv2__wordmark", i: "EGT" }
    ] },
    items: items,
    last: last,
    showExpandedMenu: false
  };
}
exports.navV2 = navV2;


function footerCol(title, links) {
  return {
    n: "nav", c: "egt-foot__col", "aria-label": title,
    i: [ { n: "h3", c: "egt-foot__h", i: title },
      { n: "ul", c: "egt-foot__list",
        i: links.map(function (l) {
          /* name wrapped so the byline DESC always drops to its own line under
           * it (founder: descriptions shouldn't sit inline next to the link). */
          var inner = [ { n: "span", c: "egt-foot__name", i: l.nm } ];
          if (l.desc) { inner.push({ n: "span", c: "egt-foot__desc", i: l.desc }); }
          return { n: "li", i: { n: "a", href: l.href,
            target: l.ext ? "_blank" : undefined, rel: l.ext ? "noopener" : undefined,
            i: inner } };
        }) } ]
  };
}

/* opts.hideFooterCapture — OPT-IN, default off, additive (every surface that
 * does not pass it keeps the footer capture exactly as it was).
 *
 * Why it exists (2026-08-21): /exploit now has exactly ONE ask, by founder
 * directive — the free opening. The footer's "Be first to know / Notify me"
 * strip landed within the same screen as the page's closing CTA, so the last
 * thing a reader saw was two email asks side by side. Worse, they are not the
 * same ask: the footer source is "footer", which is NOT in
 * app/helpers/egt.free-sample.js PROMISED_SOURCES, so signing up there does NOT
 * mint the free chapters — a reader who used the nearer box would have got a
 * newsletter instead of the book they came for. Suppressing it on that one
 * surface leaves a single, honest door. */
function footer(opts) {
  opts = opts || {};
  var year = (new Date()).getFullYear();
  return {
    n: "footer", c: "egt-foot",
    i: [
      { n: "div", c: "egt__shell egt-foot__top",
        i: [
          { n: "div", c: "egt-foot__brand",
            i: [
              { n: "a", href: "/", c: "egt-foot__wordmark", "aria-label": "EGT home",
                i: { n: "img", src: "/logo.svg", alt: "EGT", c: "egt-foot__logo" } },
              { n: "p", c: "egt-foot__line",
                i: [ { n: "span", c: "egt-foot__line-brand", i: "EGT" },
                  " — A house of studios — building the Universe." ] },
              /* WEB-2 — THE CAPTURE DOOR. This was a
               * <form action="/contact-us" method="get">: submitting an address
               * navigated the visitor to the contact page and stored NOTHING,
               * on every page of the site. It is now the shared double opt-in
               * capture (app/helpers/egt.capture.js) — stays on the page, posts
               * by fetch, and self-segments (footer → the platform newsletter). */
              (opts.hideFooterCapture === true) ? null : Capture.capture({
                source: "footer",
                id: "egt-cap-footer",
                variant: "inline",
                /* PAGE-AWARE LABEL (SEO wave 2C-12, 2026-10-09) — opt-in via
                 * opts.footerCaptureLabel; every surface that does not pass it
                 * (the homepage included) keeps "Be first to know." exactly.
                 * COPY ONLY: the source, segment, consent and double opt-in are
                 * untouched, so a label must never promise what the `footer`
                 * door does not deliver (it is NOT a free-sample source — see
                 * egt.free-sample.js PROMISED_SOURCES). */
                label: (typeof opts.footerCaptureLabel === "string" && opts.footerCaptureLabel)
                  ? opts.footerCaptureLabel : "Be first to know.",
                cta: "Notify me",
                /* NO consent tick here — FOUNDER RULING 2026-07-24 (F33), and it
                 * applies to the FOOTER ONLY: "no one else does that in their
                 * footer — we can continue to do it everywhere else." This
                 * narrows the 2026-07-22 ruling that had put the tick on every
                 * list-adding capture. The tick is opt-in PER PLACEMENT via
                 * `consent: true`, so omitting it here is the whole change — do
                 * NOT touch egt.capture.js's default, and read each call site
                 * for what that door does rather than inferring it from here.
                 *
                 * The consent artefact for a footer signup is therefore the
                 * existing DOUBLE OPT-IN, which is unaffected by this: the
                 * capture route writes marketing:false + transactionalOnly:true
                 * with consent.status "pending" and NO segment membership
                 * (app/routes/egt.email.capture.js), so nothing is mailable
                 * before confirming; the confirmation click is what writes
                 * marketing:true and consent.status "confirmed" with
                 * confirmedOn + source (app/routes/egt.email.confirm.js). The
                 * recorded `consent.affirmative` is now false for this door,
                 * which is exactly what that field means — "this door did not
                 * ask" — and it is evidence, never a gate (the server has never
                 * required it, precisely so an inline strip can omit the box). */
                note: "Letters about building EGT, the films and the Universe. We'll email you a code to confirm. Unsubscribe anytime — see the "
              })
            ].filter(Boolean) },
          { n: "div", c: "egt-foot__cols",
            i: [
              footerCol("The Studios", STUDIOS),
              footerCol("Podcasts", PODCASTS.map(function (p) { return { nm: p.nm, desc: p.desc, href: p.href }; })
                .concat([ { nm: "All podcasts", href: "/podcasts" } ])),
              footerCol("Company", [ { nm: "About", href: "/about" }, { nm: "The Founder", href: "/founder" } ]
                .concat(JOURNAL_LIVE ? [ { nm: "The Making", desc: "The founder's journal", href: "/journal" } ] : [])
                .concat([ { nm: "Join the beta", href: "/reserve" } ])),
              footerCol("Connect", [
                { nm: "Contact", href: "/contact-us" },
                { nm: "Instagram", href: "https://instagram.com/egtuniverse", ext: true },
                { nm: "YouTube", href: "https://youtube.com/@egtuniverse", ext: true }
              ]),
              footerCol("Legal", [
                { nm: "The EGT Codex", desc: "Including the Terms of Service & Privacy Policy", href: "/legal/codex" },
                { nm: "Business Associate Agreement", href: "/legal/baa" }
              ])
            ] }
        ] },
      { n: "div", c: "egt__shell egt-foot__bar",
        i: [
          { n: "p", c: "egt-foot__legalnote",
            i: [ "Subject to the Terms of Service and Privacy Policy contained in ",
              { n: "a", href: "/legal/codex", i: "The EGT Codex" }, "." ] },
          /* Site-wide trademark attribution — it exists to satisfy the §8.1
           * "Notice" clause of EVERY trademark license EGT holds. Each §8.1 asks
           * for an attribution "substantially in the form" it quotes, in "at least
           * one reasonably visible location (such as the website footer)", so one
           * combined sentence in this every-page footer discharges all of them.
           * Name each mark the way its own license's `**Mark:**` line names it —
           * those files govern, this comment does not, and the names HAVE been
           * revised before. The symbol tracks registration status: ® only while a
           * federal registration is in force, ™ for a mark that is unregistered or
           * merely pending. Never put ® on a ™ mark. The authoritative signed set
           * lives in the platform repo under
           * agent-task-tracker/research/trademark-licenses/contracts/ (read-only);
           * re-read the `**Mark:**` and §8.1 lines there before touching the
           * string below. */
          { n: "p", c: "egt-foot__legalnote",
            i: trademarkNotice() },
          { n: "p", c: "egt-foot__copy",
            i: [ "© " + year + " EGT. Built on ",
              { n: "span", c: "egt-foot__bw-hi", i: "The Universe Platform" }, " by ",
              { n: "span", c: "egt-foot__bw-gold", i: "EGT" }, "." ] }
        ] }
    ]
  };
}
exports.footer = footer;

/* Page-aware footer-capture labels (SEO wave 2C-12). Each is true of the
 * `footer` door as it stands — the ONE general newsletter, double opt-in. */
exports.FOOTER_LABELS = {
  platform: "Get the email the day self-serve opens.",
  entertainment: "New films, music and chapters from The Universe.",
  classicman: "New Classic Man episodes and news, first."
};

/* site-wide reading-progress hairline — a fixed bar pinned under the sticky
 * nav; its gold fill scales with scroll depth (driven by initReadingProgress
 * in the shared /src/egt.home.js). Rendered on EVERY page via S.page(). */
function readingProgress() {
  return { n: "div", c: "egt-doc__progress", "aria-hidden": "true",
    i: { n: "div", c: "egt-doc__fill" } };
}
exports.readingProgress = readingProgress;

/* Site-wide JSON-LD entity graph (Organization + WebSite) — the top
 * entity-SEO / AI-citation lever (before this, ONLY /forge had any JSON-LD, and
 * nothing declared the brand as an entity). Rendered on EVERY page via page().
 * The origin comes from config (my.app.url) so it follows the production domain
 * automatically once that value is set — never hardcoded (see SEO strategy:
 * production domain is a deferred founder decision). `sameAs` is intentionally
 * omitted until the founder supplies official profile URLs — no fabricated
 * links. Per-surface nodes (SoftwareApplication, Book/Product, CreativeWork,
 * WebPage/BreadcrumbList) are added by the individual page elements. */
/* The trademark attribution every footer carries — site, journal and email read
 * the same my.app.trademarkNotice from app/environment/def.config.json, so they
 * cannot drift (founder 2026-11-02: "the same line for all the footers"). */
function trademarkNotice() {
  try {
    var $ = require("../../uam/scafolding.js");
    return ((((($ || {}).config || {}).my || {}).app || {}).trademarkNotice || "").toString();
  } catch (e) { return ""; }
}
exports.trademarkNotice = trademarkNotice;

function siteOrigin() {
  var origin = "";
  try {
    var $ = require("../../uam/scafolding.js");
    origin = ((((($ || {}).config || {}).my || {}).app || {}).url || "").toString();
  } catch (e) { origin = ""; }
  return origin.replace(/\/index\.html?$/i, "").replace(/\/+$/, "");
}
exports.siteOrigin = siteOrigin;

function siteSchema() {
  var origin = siteOrigin();
  var graph = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "Organization", "@id": origin + "/#organization",
        name: "EGT", alternateName: "egt.studio", url: origin + "/",
        logo: { "@type": "ImageObject", url: origin + "/logo.svg" },
        description: "One studio across software, AI, creative, apps, and entertainment.",
        /* Official brand profiles (the SAME accounts linked in the footer, so
         * nothing fabricated) — entity anchors for AI citation. */
        sameAs: [ "https://instagram.com/egtuniverse", "https://youtube.com/@egtuniverse" ] },
      { "@type": "WebSite", "@id": origin + "/#website",
        url: origin + "/", name: "EGT",
        publisher: { "@id": origin + "/#organization" } }
    ]
  };
  return { n: "script", type: "application/ld+json", i: JSON.stringify(graph) };
}
exports.siteSchema = siteSchema;

/* Build a JSON-LD <script> node from a schema.org @graph array — used by
 * individual page elements for their per-surface schema (WebPage, Breadcrumb,
 * SoftwareApplication, Book/Product, CreativeWork …). */
exports.jsonLd = function jsonLd(graph) {
  /* <, > and & are written as \u003c / \u003e / \u0026 (identical JSON). Script
   * contents are emitted raw, and the renderer decodes HTML entities in text, so
   * escaping & too means no string value — e.g. a title — can ever close this
   * <script> block, literally or entity-encoded. U+2028/2029 likewise. */
  return { n: "script", type: "application/ld+json",
    i: JSON.stringify({ "@context": "https://schema.org", "@graph": [].concat(graph) })
      .replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026")
      .replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029") };
};

/* The plain per-surface pair — a WebPage (or AboutPage / ContactPage) bound to
 * the site-wide WebSite, plus its BreadcrumbList — for surfaces that carry no
 * richer entity of their own (SEO wave 2B-7, 2026-10-09). Hygiene only: no
 * offers, no ratings, no dates.
 *   opts.path   — the surface path, e.g. "/platform/site"
 *   opts.name   — the page's name (its title, minus the brand suffix)
 *   opts.type   — "WebPage" (default) | "AboutPage" | "ContactPage"
 *   opts.about  — optional path#fragment of the entity the page is about
 *   opts.crumbs — the trail AFTER Home: [{ name, path }]; the last crumb is
 *                 the page itself and carries no `item` (Google's pattern). */
exports.pageSchema = function pageSchema(opts) {
  var O = siteOrigin();
  var url = O + opts.path;
  var page = { "@type": opts.type || "WebPage", "@id": url + "#webpage", url: url,
    name: opts.name,
    isPartOf: { "@id": O + "/#website" },
    breadcrumb: { "@id": url + "#breadcrumb" } };
  if (opts.about) { page.about = { "@id": O + opts.about }; }
  var trail = [ { name: "Home", path: "/" } ].concat(opts.crumbs || []);
  var items = trail.map(function (c, i) {
    var li = { "@type": "ListItem", position: i + 1, name: c.name };
    if (i < trail.length - 1) { li.item = O + c.path; }
    return li;
  });
  return exports.jsonLd([ page,
    { "@type": "BreadcrumbList", "@id": url + "#breadcrumb", itemListElement: items } ]);
};

/* N2 — the announcement bar (egt.announce), a registered app element that
 * renders a restrained single-line frosted strip ABOVE the nav. It is emitted
 * as the FIRST header node (before navV2) so it sits above the brand on every
 * page that renders the EGT chrome. The element renders NOTHING (an empty
 * string) when no item is in-window, so the section simply does not appear. */
function announceBar() {
  return { n: "egt.announce" };
}
exports.announceBar = announceBar;

/* The announcement bar ships on EVERY surface, so its client behaviour has to
 * as well — emitted here (same rationale as navScript / captureScript) so a new
 * page can never ship a bar that doesn't rotate. It no-ops when the bar isn't
 * present. */
function announceScript() {
  return { n: "script", src: "/src/egt.announce.js", type: "text/javascript", defer: "defer" };
}
exports.announceScript = announceScript;

/* page wrapper: nav + sections + footer under one .egt root ---------- */
exports.page = function page(reserveUrl, sections, opts) {
  opts = opts || {};
  /* navV2 (the shared ua.navbar framework control) is now the ONLY nav on
   * every page — the bespoke nav()/mobileMenu()/platformPanel() are retired.
   * announceBar() is inserted ABOVE navV2 so the announcement strip sits above
   * the brand site-wide (N2) — UNLESS the page opts out (opts.hideAnnounce), as
   * the conversion-first /exploit/free landing does so the rotating promo can't
   * compete with the single free offer. Opt-in and additive: every other surface
   * (default unset) keeps the banner. */
  var header = opts.hideAnnounce === true
    ? [ navV2(reserveUrl, opts) ]
    : [ announceBar(), navV2(reserveUrl, opts) ];
  /* opts.noNav: the page brings its own navigation (the /ig side rail), so no top
   * bar and no announcement strip. The footer still ships. */
  if (opts.noNav === true) { header = []; }
  /* The "Join the Beta" whoosh client ships ONLY on the beta-CTA surfaces — the
   * exact pages whose navV2() renders the default beta CTA (opts.betaCta:true,
   * not a custom navCta override, not force-hidden). Emitting it here — the same
   * injection pattern as navScript/announceScript/captureScript — keeps it in
   * lockstep with the CTA it animates: a page can never show the beta CTA without
   * the tease, nor ship the tease on a page that has no CTA to whoosh. */
  var betaCtaTail = (opts.betaCta === true && !opts.navCta && !opts.hideNavCta)
    ? [ betaCtaScript() ] : [];
  return { n: "main", c: "egt",
    i: [ readingProgress() ].concat(header)
      /* The capture client ships with the FOOTER, not from each page's JSON.
       * The footer carries a capture on every surface, so the script has to be
       * on every surface — emitting it here means a new page can never ship a
       * dead capture because someone forgot a <script> line in its JSON. */
      .concat(sections).concat([ footer(opts), navScript(), announceScript(), Capture.captureScript(), siteSchema() ]).concat(betaCtaTail) };
};

/* The nav "Join the Beta" whoosh tease (/src/egt.beta-cta.js) — see page()
 * above for why it is emitted here rather than from each page's JSON, and why
 * only on the beta-CTA surfaces. It is a no-op if included twice (global guard)
 * and static under prefers-reduced-motion. */
function betaCtaScript() {
  return { n: "script", src: "/src/egt.beta-cta.js", type: "text/javascript", defer: "defer" };
}
exports.betaCtaScript = betaCtaScript;

/* The nav ships on EVERY surface, so its client behaviour has to as well —
 * emitted here (same rationale as captureScript above) so a new page can never
 * ship a nav whose Platform menu doesn't open. */
function navScript() {
  return { n: "script", src: "/src/egt.nav.js", type: "text/javascript", defer: "defer" };
}
exports.navScript = navScript;

/* content section builders ------------------------------------------ */
function reveal(node, extra) { node.c = (node.c || "") + " egt-reveal" + (extra ? " " + extra : ""); return node; }

exports.pageHero = function pageHero(opts) {
  var inner = [ { n: "p", c: "egt__eyebrow egt-reveal", i: opts.eyebrow } ];
  inner.push({ n: "h1", c: "egt-phero__title egt-reveal", i: opts.title });
  if (opts.lead) { inner.push({ n: "p", c: "egt-phero__lead egt-reveal", i: opts.lead }); }
  if (opts.cta) { inner.push({ n: "div", c: "egt-phero__actions egt-reveal", i: opts.cta }); }
  return { n: "section", c: "egt-phero" + (opts.c ? " " + opts.c : ""),
    i: { n: "div", c: "egt__shell", i: inner } };
};

exports.statement = function statement(text, opts) {
  opts = opts || {};
  return { n: "section", c: "egt-section" + (opts.center ? " egt-section--center" : ""),
    i: { n: "div", c: "egt__shell", i: reveal({ n: "p", c: "egt-lead", i: text }) } };
};

exports.quote = function quote(text, attr) {
  return { n: "section", c: "egt-section egt-section--center",
    i: { n: "div", c: "egt__shell",
      i: reveal({ n: "blockquote", c: "egt-quote",
        i: [ text ].concat(attr ? [ { n: "footer", c: "egt-quote__attr", i: attr } ] : []) }) } };
};

exports.section = function section(opts) {
  var head = [];
  if (opts.eyebrow) { head.push({ n: "p", c: "egt__eyebrow", i: opts.eyebrow }); }
  if (opts.title) { head.push({ n: "h2", c: "egt-section__title", i: opts.title }); }
  if (opts.lead) { head.push({ n: "p", c: "egt-section__lead", i: opts.lead }); }
  var children = [ reveal({ n: "div", c: "egt-section__head", i: head }) ];
  if (opts.body) { children = children.concat(opts.body); }
  return { n: "section", c: "egt-section" + (opts.c ? " " + opts.c : ""),
    i: { n: "div", c: "egt__shell", i: children } };
};

exports.featureGrid = function featureGrid(items) {
  return reveal({ n: "div", c: "egt-grid",
    i: items.map(function (it) {
      var inner = [];
      inner.push({ n: "h3", c: "egt-feat__title", i: it.title });
      inner.push({ n: "p", c: "egt-feat__copy", i: it.copy });
      return { n: "article", c: "egt-feat", i: inner };
    }) });
};

/* WP-N2: the reserve wizard moved to the dedicated /reserve page (rendered
 * inline there). Reserve CTAs are now plain LINKS to that page — the third
 * `reserve` arg is retained for call-site compatibility but no longer attaches
 * any dialog-opener attributes (callers already pass the /reserve href). This is
 * the single lever that converts every page's reserve CTA to a normal link. */
exports.cta = function cta(href, label, reserve) {
  return { n: "a", href: href, c: "egt-cta", i: [ label, { n: "span", c: "egt-cta__tick", i: " ›" } ] };
};

/* =====================================================================
 * VIEW TOGGLE — list <-> card, replicated from the NEW platform's standard
 * single dashboard view toggle (platform/universe-elements: ua.dashboard.toolbar
 * viewToggleSingleControl + iconSvgNode). One button flips card<->list and shows
 * the glyph of the view it will switch TO. Lucide-style stroke icons (viewBox 24,
 * stroke currentColor) recolor with the palette. Shared by Home + Studios; the
 * client behavior + intro demo live in app/public/src/egt.home.js
 * (initViewToggle, keyed off [data-egt-view-root]/[data-egt-view-toggle]).
 * ===================================================================== */
function viewIcon(kind) {
  var base = { n: "svg", c: "egt-view__svg", viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", "stroke-width": "2", "stroke-linecap": "round",
    "stroke-linejoin": "round", "aria-hidden": "true", focusable: "false" };
  if (kind === "grid") {
    base.i = [
      { n: "rect", x: "3", y: "3", width: "7", height: "7", rx: "1" },
      { n: "rect", x: "14", y: "3", width: "7", height: "7", rx: "1" },
      { n: "rect", x: "3", y: "14", width: "7", height: "7", rx: "1" },
      { n: "rect", x: "14", y: "14", width: "7", height: "7", rx: "1" }
    ];
  } else {
    base.i = [
      { n: "path", d: "M8 6h13" }, { n: "path", d: "M8 12h13" }, { n: "path", d: "M8 18h13" },
      { n: "path", d: "M3 6h.01" }, { n: "path", d: "M3 12h.01" }, { n: "path", d: "M3 18h.01" }
    ];
  }
  return base;
}
exports.viewIcon = viewIcon;
/* `start` = the resting view ("list" or "card"); the button renders the glyph of
 * the OTHER view (what it will switch to). */
exports.viewToggle = function viewToggle(start) {
  var current = start === "card" ? "card" : "list";
  var next = current === "card" ? "list" : "card";
  return {
    n: "button", type: "button", c: "egt-view", "data-egt-view-toggle": "true",
    "data-egt-view-current": current, "data-egt-view-next": next,
    "aria-label": next === "list" ? "Switch to list view" : "Switch to card view",
    i: { n: "span", c: "egt-view__icon", i: viewIcon(next === "list" ? "list" : "grid") }
  };
};

/* =====================================================================
 * FROSTED-GLASS-OVER-IMAGERY PRIMITIVE — the "canvas".
 *
 * The house rule: no solid-color-only sections. Every surface can sit on
 * beautiful imagery (an <img>) or poster-first <video>, with frosted,
 * readable content floating on top (.egt-glass). Artwork breathes; the
 * glass carries the words. NEVER a dark overlay / gradient scrim — contrast
 * comes from the frosted card itself, never from dimming the media.
 *
 * media() — builds the background layer (img OR poster-first video).
 *   opts: { img, video, poster, alt, parallax, fixed, priority }
 *   - video: poster-first, muted/loop/playsinline, paused on mobile/reduced
 *     motion via .egt-canvas__media--video (CSS hides <video>, shows poster).
 *   - parallax: adds data-egt-parallax for the optional scroll util.
 *
 * canvas() — a full section wrapper: <section class="egt-canvas"> with a
 *   media background and a `.egt-glass` content card on top. Drop-in for any
 *   surface; takes the same content nodes you'd pass elsewhere.
 *     opts: { media:{...}, content:[...], align, width, c, id, tall }
 *       align: "start"|"center"|"end" (vertical placement of the card)
 *       width: "narrow"|"normal"|"wide" (glass max-width)
 *       tall:  true => min-height ~ a hero band
 *
 * glass() — just the frosted card, to compose inside your own section.
 *     opts: { content:[...], width, tone, c }
 *       tone: "default"|"dark"|"light" (card translucency target)
 *
 * These are pure UA/JSON builders — no behavior. The optional parallax/tilt
 * utilities live in app/public/src/egt.home.js (shared) keyed off data-attrs.
 * ===================================================================== */

function media(opts) {
  opts = opts || {};
  var inner;
  /* SEO wave 2A-8 (2026-10-09): OPTIONAL intrinsic size of the still (img or
   * poster), emitted as width/height attributes only when a caller passes BOTH
   * (as strings — never null, which drops the node). Layout is unchanged: the
   * CSS sizes .egt-canvas__img absolutely (100% x 100%, object-fit: cover); the
   * attributes only give the browser the aspect ratio up front. Callers that
   * pass nothing (the homepage among them) render byte-identical markup. */
  var hasDims = opts.width && opts.height;
  var dimW = hasDims ? String(opts.width) : undefined;
  var dimH = hasDims ? String(opts.height) : undefined;
  if (opts.video) {
    /* an explicit opts.poster always wins; otherwise auto-use whatever the
     * media manager's "Set thumbnail" has captured for this video, if any
     * (app/routes/egt.media.js poster/capture|upload -> egt.media-inventory
     * posterFor()) — set once in the tool, used everywhere automatically. */
    var autoPoster = opts.poster || mediaInventory.posterFor(opts.video);
    var videoEl = {
      n: "video", c: "egt-canvas__video", muted: true, loop: true,
      playsinline: true, autoplay: true, preload: "none",
      poster: autoPoster || undefined, "aria-hidden": "true",
      i: { n: "source", src: opts.video, type: "video/mp4" }
    };
    /* a real <img> poster SIBLING, not just the <video poster> attribute —
     * the mobile + reduced-motion CSS below hides the whole <video> element
     * (.egt-canvas__media--video .egt-canvas__video{display:none}) to save
     * battery/bandwidth, and a hidden video's poster attribute hides right
     * along with it. Without this sibling, every video hero rendered flat
     * black on mobile / under reduced-motion instead of falling back to its
     * still frame. */
    inner = autoPoster
      ? [ { n: "img", c: "egt-canvas__img", src: autoPoster, alt: opts.alt || "",
          width: dimW, height: dimH,
          loading: opts.priority ? undefined : "lazy",
          fetchpriority: opts.priority ? "high" : undefined, decoding: "async" },
        videoEl ]
      : videoEl;
  } else {
    inner = {
      n: "img", c: "egt-canvas__img", src: opts.img, alt: opts.alt || "",
      width: dimW, height: dimH,
      loading: opts.priority ? undefined : "lazy",
      fetchpriority: opts.priority ? "high" : undefined,
      decoding: "async"
    };
  }
  var node = {
    n: "div",
    c: "egt-canvas__media" + (opts.video ? " egt-canvas__media--video" : "") +
       (opts.fixed ? " egt-canvas__media--fixed" : ""),
    "aria-hidden": "true", i: inner
  };
  if (opts.parallax) { node["data-egt-parallax"] = String(opts.parallax === true ? 0.12 : opts.parallax); }
  return node;
}
exports.media = media;

function glass(opts) {
  opts = opts || {};
  var cls = "egt-glass";
  if (opts.width === "narrow") { cls += " egt-glass--narrow"; }
  else if (opts.width === "wide") { cls += " egt-glass--wide"; }
  if (opts.tone === "dark") { cls += " egt-glass--dark"; }
  else if (opts.tone === "light") { cls += " egt-glass--light"; }
  if (opts.c) { cls += " " + opts.c; }
  return { n: "div", c: cls, i: opts.content };
}
exports.glass = glass;

function canvas(opts) {
  opts = opts || {};
  var cls = "egt-section egt-canvas";
  if (opts.align === "center") { cls += " egt-canvas--center"; }
  else if (opts.align === "end") { cls += " egt-canvas--end"; }
  if (opts.tall) { cls += " egt-canvas--tall"; }
  if (opts.c) { cls += " " + opts.c; }
  var node = { n: "section", c: cls, i: [
    media(opts.media || {}),
    { n: "div", c: "egt__shell egt-canvas__inner",
      i: glass({ content: opts.content, width: opts.width, tone: opts.tone }) }
  ] };
  if (opts.id) { node.id = opts.id; }
  return node;
}
exports.canvas = canvas;

/* =====================================================================
 * LISTEN — album art at the front of the title, with Apple Music + Spotify +
 * YouTube Music LOGO links for click-through (icons only — no text labels).
 * No player, no preview, no audio (founder rule).
 *
 * ALBUMS — the single source of truth, shared by home + entertainment so the two
 * surfaces never drift. Works under EGT Studios (ASCAP). Optimized covers in
 * /imgs/albums/ (originals kept in /album-art/). Real links; the Apple URLs for
 * Blueprints / This is My Christmas were swapped in the founder's note (the URL
 * slugs prove it) and are corrected here.
 *
 * Exploit: Act 1 released 2026-07-24 and its Apple + Spotify ALBUM pages resolve,
 * so both point at the album. YouTube now resolves too: the release is published
 * on YouTube Music, so its link points at the album PLAYLIST (the earlier
 * main-channel exception no longer applies). Title is `Exploit: Act 1` by decision #81
 * in the Exploit repo: the act name is deliberately NOT in the title, because
 * "Exploit: The Child" reads as an imperative once the punctuation drops out of a
 * search result or share preview.
 * Never place "Exploit" next to a noun phrase it can govern as a verb.
 * ===================================================================== */
/* Each album carries `releaseDate` (ISO, authoritative from
 * task-tracker/research/nav-albums-diagnosis.md's COMPLETE TRACKLISTS) and an
 * ordered `tracks` array. The three EXPLOIT records carry per-track objects
 * `{ title, pov, feeling }` — the character POV + a one-line feeling/credit
 * descriptor (instrumentals have no `pov`); the other seven carry plain title
 * strings. This is the SINGLE SOURCE both /entertainment and the per-album
 * detail pages read (see egt.album.js). A PRERELEASE (a future `releaseDate`)
 * additionally carries `presaveUrl` (the real DistroKid HyperFollow) + `window`
 * (the founder-authorized MONTH window shown on the /entertainment card — a
 * specific date is only shown on the auto-flipping detail page); both keys are
 * REMOVED the moment the record ships, so a released work never offers a
 * pre-save. Every record in this list is released today. The
 * released/prerelease posture is NOT hand-toggled: albumReleased() compares
 * `releaseDate` to now at render time, and albumStreamable() additionally
 * requires ≥1 streaming link — so a release day is "add apple/spotify/youtube,
 * delete window/presaveUrl". Do NOT invent track data — every row is from the
 * diagnosis doc. */
var ALBUMS = [
  { id: "exploit-act-1", nm: "Exploit: Act 1", meta: "Songs from the novel · Book One of The Universe",
    art: "/imgs/albums/exploit-act-1.jpg", releaseDate: "2026-07-24",
    apple: "https://music.apple.com/us/album/exploit-act-1/6793208951",
    spotify: "https://open.spotify.com/album/0Kt3qvchAFGVlcrCZICdo1",
    youtube: "https://www.youtube.com/playlist?list=OLAK5uy_lAFJQZJjrbj2KMmLGGqqgySxWdqjLMNcM",
    tracks: [
      { title: "The Child Theme", feeling: "The theme stated pure and warm." },
      { title: "Window Seat", pov: "Niko Zhelyazko", feeling: "An older brother's self-erasing provision as love." },
      { title: "The Whole Country in the Window", pov: "Kaawa", feeling: "A child's boundless wonder — happy in a way that will hurt later." },
      { title: "Hairline", pov: "Cole", feeling: "Grief as a fracture so fine no one saw it break." },
      { title: "Silk (For the One Who Left)", pov: "Cole", feeling: "The letter to the one who left; a grip released." },
      { title: "You Found the Body Still Alive", pov: "Cole", feeling: "Disbelief and gratitude at being reached." },
      { title: "Field Hospital", pov: "Cole", feeling: "Private grief turned communal roar; the first big lift of hope." },
      { title: "Ashes Have a Pulse", pov: "Cole", feeling: "Grief refusing the last word; open rebellion." },
      { title: "Through Me First", pov: "Ronan", feeling: "Chosen family as the wall; joyful, defiant loyalty." },
      { title: "Featureless Distance", pov: "Jonathan Avery Karros", feeling: "Despair at the ledge; the true bottom." },
      { title: "Being-Alive Had to Go Somewhere", pov: "Bea", feeling: "The living world at full flame, one breath before it's taken." },
      { title: "Green", pov: "Bea", feeling: "A young life severed mid-sentence; the shattering." },
      { title: "I Never Left", pov: "The Friend", feeling: "The felt presence returning; the single ember Act One closes on." }
    ] },
  /* Act 2 — RELEASED 2026-08-07 (auto-flip at its releaseDate → streaming
   * posture, same as Act 1). Apple + Spotify album pages resolve. YouTube is NOT
   * on YouTube Music yet, so its link points at the artist MAIN channel for now
   * (founder ruling 2026-08-07 — the same interim Act 1 used at its launch); swap
   * to the album PLAYLIST url once it publishes on YT Music.
   * UPDATE 2026-10-09 (SEO wave 2B-6): the album playlist is now live on
   * YouTube (verified on the artist's releases tab; playlist title matches).
   * It is carried as `youtubeAlbum` and used ONLY as the JSON-LD sameAs on
   * /entertainment and the album page. The visible `youtube` pill is left on
   * the channel because the homepage reads this same record (homepage changes
   * need founder approval); swap `youtube` to the playlist when approved. */
  { id: "exploit-act-2", nm: "Exploit: Act 2", meta: "Songs from the novel · Book One of The Universe",
    art: "/imgs/albums/exploit-act-2.jpg", releaseDate: "2026-08-07",
    apple: "https://music.apple.com/us/album/exploit-act-2/6795268783",
    spotify: "https://open.spotify.com/album/6QeXiSSGvbOHYepWmAyefF",
    youtube: "https://www.youtube.com/@egtuniverse",
    youtubeAlbum: "https://www.youtube.com/playlist?list=OLAK5uy_m3BK3VRTr6B5-8wvbptb8Q9w9nvyb263o",
    tracks: [
      { title: "Cue the Music (I'm About to Ruin Someone's Day)", pov: "Ronan", feeling: "Comedic tough-guy swagger; loyalty in a party costume." },
      { title: "The One Who Comes", pov: "Reyna Karros", feeling: "Mama-bear devotion with claws." },
      { title: "Beside You, Where You Couldn't See", pov: "The Friend", feeling: "Unglamorous, patient fidelity through unseen years." },
      { title: "A Healer of Situations", pov: "Carleton Gunther", feeling: "Restraint as a vocation; the man who won't draw." },
      { title: "Weather, Not a Leash", pov: "Jonathan Avery Karros", feeling: "A power he can't summon, trust, or put down; the storm that never breaks." },
      { title: "A Healer, Not a Killer", pov: "The Friend", feeling: "Gentleness as a nature, not a weakness." },
      { title: "Best Family You Can Pick", pov: "Ronan", feeling: "The misfits who picked each other; the warmth before the fall." },
      { title: "The Warm Car", pov: "Niko Zhelyazko", feeling: "A devil's bargain made out of love; the comfort that IS the trap." },
      { title: "I'd Rather Have the Killer", pov: "Reyna Karros", feeling: "The rift that cracks and does not mend." },
      { title: "My Arms Went Through You", pov: "Kaawa", feeling: "Reaching for the thing always there and closing on air." },
      /* Released title per DistroKid (the earlier "The Ground Held My Hand"
       * rename is applied upstream); POV re-identified to Bea per the author.
       * Feeling kept POV-neutral (from the credit doc's musical intent) because
       * the POV attribution is the one unconfirmed point — see report. */
      { title: "Everything, and Still Warm From Having It", pov: "Bea", feeling: "One bright thread within the machine — kept, not used." },
      { title: "Here We Go Again", pov: "Ronan", feeling: "Adrenaline-joy into the chase." },
      { title: "No Green", pov: "Jonathan Avery Karros", feeling: "The hand that could heal stays dark; all is lost." },
      { title: "The Machine That Never Sleeps", feeling: "The mechanism locks into a cold ostinato." }
    ] },
  /* Push Past Pain — RELEASED Fri 2026-08-14 (sits before Act 3's 08-21).
   * Writer/lyrics: Isaiah Carter (a real person — the songs are drawn from his
   * true life experiences); PERFORMED under the house artist (The Universe, EGT),
   * produced by Justin K Kazmierczak, EGT Studios — normal album rules.
   *   apple/spotify/youtube — the release-specific URLs (added on release
   *     2026-08-14). Now that the album's OWN links are present AND the record is
   *     released, the album streaming pills take over and the "Follow the artist"
   *     row hides (egt.album.js). YouTube is not yet an album playlist on YT
   *     Music, so it points at the artist MAIN channel for now (same interim Act
   *     1/Act 2 used); swap to the album PLAYLIST url once it publishes on YT
   *     Music. (2026-10-09: the playlist is live — carried as `youtubeAlbum`
   *     for JSON-LD sameAs only; see the Act 2 note. SEO wave 2B-6.)
   *   `explicit: true` — every track on this record carries the "E" explicit
   *     mark (rendered per-row by egt.trackrow.js).
   *   `artist` — the HOUSE ARTIST pages, kept as the pre-release fallback (only
   *     rendered while a record has no own links / is unreleased).
   * (The former per-album `newsletter` opt-in is gone: as of 2026-08-21 EVERY
   * album detail page carries the free-sample capture unconditionally — see
   * egt.album.js — so the flag had no readers left.) */
  { id: "push-past-pain", nm: "Push Past Pain", meta: "Based on the true life experiences of Isaiah Carter",
    art: "/imgs/albums/push-past-pain.jpg", releaseDate: "2026-08-14",
    explicit: true,
    apple: "https://music.apple.com/us/album/push-past-pain-ep/6800568299",
    spotify: "https://open.spotify.com/album/4RoPjiMuG0mZjjJFwTwdUZ",
    youtube: "https://www.youtube.com/@egtuniverse",
    youtubeAlbum: "https://www.youtube.com/playlist?list=OLAK5uy_l-fP5hxl9sqW0bqkPPP57EldUNxj45Nlk",
    artist: {
      apple: "https://music.apple.com/us/artist/the-universe-egt/1858007635",
      spotify: "https://open.spotify.com/artist/0GrnoT0c911oBpvHqbTawa",
      youtube: "https://music.youtube.com/channel/UCtrNIgPSQr8MfrYuOtMA7vg"
    },
    tracks: [ "Push Past Pain", "Under the Bar", "Make a Diamond", "Gains",
      "Home Became a Destination", "Fusion" ] },
  /* Act 3 — RELEASED 2026-08-21 (auto-flip at its releaseDate → streaming
   * posture, same as Acts 1 and 2). Apple + Spotify album pages resolve. YouTube
   * is NOT on YouTube Music yet, so its link points at the artist MAIN channel
   * for now (founder ruling 2026-08-21 — the same interim Act 1 and Act 2 used at
   * their launches); swap to the album PLAYLIST url once it publishes on YT
   * Music. The prerelease `window` + DistroKid `presaveUrl` were removed on
   * release: a released record must never offer a pre-save.
   * UPDATE 2026-10-09 (SEO wave 2B-6): the album playlist is now live on
   * YouTube (verified on the artist's releases tab; playlist title matches).
   * It is carried as `youtubeAlbum` and used ONLY as the JSON-LD sameAs on
   * /entertainment and the album page. The visible `youtube` pill is left on
   * the channel because the homepage reads this same record (homepage changes
   * need founder approval); swap `youtube` to the playlist when approved. */
  { id: "exploit-act-3", nm: "Exploit: Act 3", meta: "Songs from the novel · Book One of The Universe",
    art: "/imgs/albums/exploit-act-3.jpg", releaseDate: "2026-08-21",
    apple: "https://music.apple.com/us/album/exploit-act-3/6795340363",
    spotify: "https://open.spotify.com/album/3MIWF1da3v6uiKqjm90ZYA",
    youtube: "https://www.youtube.com/@egtuniverse",
    youtubeAlbum: "https://www.youtube.com/playlist?list=OLAK5uy_nEYh3D96nP4aqmKW9m7IL3t3MGSe5LM2k",
    tracks: [
      { title: "Blunt Instrument", pov: "Niko Zhelyazko", feeling: "The machine at its apex, the last cold; grief become armor." },
      { title: "Behind the Eyes", pov: "Jonathan Avery Karros", feeling: "The turn from commanding to being carried; scores the resurrection." },
      { title: "Brick by Furious Brick", pov: "Reyna Karros", feeling: "What the fierceness cost; the wall poured on purpose, still clamped." },
      { title: "A Child's Kindness", pov: "Reyna Karros", feeling: "The hardest woman cracked by a smaller, gentler force; she takes the hand." },
      { title: "Just Ride", pov: "Carleton Gunther", feeling: "The good death sung from inside the leaving; dignity, “just ride.”" },
      { title: "Before the Grey Chevrolet", pov: "Carleton Gunther", feeling: "A man who gave even his own story away." },
      { title: "A Post, A Watch", pov: "Reyna Karros", feeling: "The guardian reframe — that's not the killer in you, that's the guard." },
      { title: "The Cold Comes Out", pov: "Reyna Karros & Carleton Gunther", feeling: "The fifty-year cold gone; a warm hand with no knife." },
      { title: "Load-Bearing Walls", pov: "Carleton Gunther", feeling: "The fixer rolls up his sleeves; purpose, not triumph." },
      { title: "First One Awake", pov: "Bea", feeling: "The first conscious soul awake; deciding to become the answer." },
      { title: "We Cross Together", pov: "Bea", feeling: "The welcomer's song; she walks a stranger across." },
      { title: "Gold at the Roots", pov: "Bea", feeling: "Finds her own murdered family waking; no one grieves alone." },
      { title: "A Green Light Still Turns", pov: "Bea", feeling: "The green light turns now as a kept promise." },
      { title: "The Weight I Carry In", pov: "The Friend", feeling: "The one song allowed to glow; convergence warmth." },
      { title: "Say No If You Want To", pov: "The Friend", feeling: "The open hand; grace offered, never forced." },
      { title: "I Kept It", pov: "Niko Zhelyazko", feeling: "The fifty-year vigil; a worn coin never thrown away." },
      { title: "Come and Get It", pov: "Niko Zhelyazko", feeling: "The break; the cold machine thaws into a man; the climax." },
      { title: "Fall Becomes Flight", pov: "Jonathan Avery Karros", feeling: "“I was never falling — I was held the whole way down.” Hope, the last word." },
      { title: "The Lullaby That Completes", feeling: "The Child Theme, healed and whole." }
    ] },
  { id: "sound-of-freedom", nm: "The Sound of Freedom", meta: "Music from the conference",
    art: "/imgs/albums/the-sound-of-freedom.jpg", releaseDate: "2026-07-04",
    apple: "https://music.apple.com/us/album/the-sound-of-freedom-music-from-the-conference-single/6786539729",
    spotify: "https://open.spotify.com/album/7uhTm4lhS6USK3PEiVBbsK",
    youtube: "https://music.youtube.com/playlist?list=OLAK5uy_nfqbGAPJroWmtf3lkHSW80xcQC2n6SSSA",
    tracks: [ "Dominion", "Reclaim", "Pour Out" ] },
  { id: "anchored", nm: "Anchored", meta: "Written from standing on faith in the midst of challenging circumstances",
    art: "/imgs/albums/anchored.jpg", releaseDate: "2026-05-04",
    apple: "https://music.apple.com/us/album/anchored/6767388904",
    spotify: "https://open.spotify.com/album/6fKddBx72MOne6gKpl3QWY",
    youtube: "https://music.youtube.com/playlist?list=OLAK5uy_kaLbz_n4WPcEhYbj3_cPZU1sexW9UeKVU",
    tracks: [ "A Note from The Author", "Anchored", "Ashes Wear a Crown", "Coffee Cold",
      "Draft in Here", "Heart of Humanity", "I Remain", "Not a Memory", "Uncharted", "Devil, May I" ] },
  { id: "ident", nm: "Ident", meta: "Music from the keynote",
    art: "/imgs/albums/ident.jpg", releaseDate: "2026-04-03",
    apple: "https://music.apple.com/us/album/ident-music-from-the-keynote/1889701531",
    spotify: "https://open.spotify.com/album/0o0edK1ubo9LT7P39406rM",
    youtube: "https://music.youtube.com/playlist?list=OLAK5uy_mUgHq3bsGJMbXw3G3tmk7KjOhJl2QB__0",
    tracks: [ "Elaboration", "How the Internet Works", "Namespace", "Stallion", "Application Artisan",
      "Elements", "Universe", "Intrepid", "Reach", "UAT", "Definitions",
      "From the House to the Office", "Remote Warrior", "Keynote" ] },
  { id: "solarys", nm: "Sol'arys", meta: "Music from the series",
    art: "/imgs/albums/solarys.jpg", releaseDate: "2026-01-08",
    apple: "https://music.apple.com/us/album/solarys-music-from-the-series/1866056444",
    spotify: "https://open.spotify.com/album/4Pc8UaFQkBf7YWUipqstKy",
    youtube: "https://music.youtube.com/playlist?list=OLAK5uy_km4B87trRn-K0mecJjwBfesf4rDNZW7No",
    tracks: [ "Integrity Preserved", "Carnate", "Hope Has a Name", "Nexus Event", "No One Dies",
      "Partnership", "The Adventure", "The Printer Room", "The War", "What Remains" ] },
  { id: "shadows", nm: "Shadows", meta: "Music from the film",
    art: "/imgs/albums/shadows.jpg", releaseDate: "2025-11-30",
    apple: "https://music.apple.com/us/album/shadows-music-from-the-film/1858046788",
    spotify: "https://open.spotify.com/album/002eSXH070OMIPExquwk4d",
    youtube: "https://music.youtube.com/playlist?list=OLAK5uy_l20bpksI0qo_I5Di4XSenJMQQ1viNwlgw",
    tracks: [ "Told Johnny", "24,000", "Reborn", "It's All Off", "Bringing Her Home",
      "PTSD", "Sniper's Mouse", "Who The Hell Are You" ] },
  { id: "blueprints", nm: "Blueprints", meta: "Single",
    art: "/imgs/albums/blueprints.jpg", releaseDate: "2025-11-30",
    apple: "https://music.apple.com/us/album/blueprints-single/1858007634",
    spotify: "https://open.spotify.com/album/5eb3Sx9chLRgyhX59fLw8g",
    youtube: "https://music.youtube.com/playlist?list=OLAK5uy_lgc7BtMfUykG0oosS4Uf53-Y3Wpqzbxw8",
    tracks: [ "Blueprints" ] },
  { id: "christmas", nm: "This is My Christmas", meta: "Single",
    art: "/imgs/albums/christmas.jpg", releaseDate: "2025-12-07",
    apple: "https://music.apple.com/us/album/this-is-my-christmas-single/1858567811",
    spotify: "https://open.spotify.com/album/5gkpwXSUMB2nynAfy3xUT2",
    youtube: "https://music.youtube.com/playlist?list=OLAK5uy_keZ2IEZnUi_SZhT0Fm3Z1wquzeg_A42pQ",
    tracks: [ "This Is My Christmas" ] }
];
exports.ALBUMS = ALBUMS;

/* AUTO-FLIP — the ONE date comparison that decides an album's posture at RENDER
 * time (no manual toggle). A `releaseDate` in the FUTURE → prerelease (presave);
 * today/past → released (streaming). Parsed at NOON UTC so a bare YYYY-MM-DD is
 * never shifted across a day by the server's timezone. An album with no/invalid
 * date is treated as released (legacy safety). */
function albumReleased(a) {
  if (!a || !a.releaseDate) { return true; }
  var t = Date.parse(a.releaseDate + "T12:00:00Z");
  if (isNaN(t)) { return true; }
  return t <= Date.now();
}
exports.albumReleased = albumReleased;

/* STREAMABLE — the posture predicate every surface should use to decide whether
 * to render icon-only streaming pills. An album is streamable only when it is
 * BOTH released (auto-flip past its releaseDate) AND carries at least one
 * streaming link. This closes the gap between albumReleased and "has links": a
 * record whose date has passed but that has no apple/spotify/youtube yet (e.g.
 * Act 3 once 2026-08-21 arrives but before its links are added) is released but
 * NOT streamable, so it must show the presave posture — never an empty pills
 * box. Pills-vs-not decisions key off THIS; the "Releases on <date>" future
 * copy keys off albumReleased (past-dated records never show a future date). */
function albumStreamable(a) {
  return albumReleased(a) && !!(a && (a.apple || a.spotify || a.youtube));
}
exports.albumStreamable = albumStreamable;

/* Long, human release date ("August 7, 2026") from an ISO YYYY-MM-DD, formatted
 * timezone-stably (UTC) so the day never drifts. Shown ONLY on the auto-flipping
 * detail page for a prerelease ("Releases on <date>"); released works show no
 * date (agents.md §4). Returns "" if the date is absent/invalid. */
function albumReleaseDateLong(a) {
  if (!a || !a.releaseDate) { return ""; }
  var t = Date.parse(a.releaseDate + "T12:00:00Z");
  if (isNaN(t)) { return ""; }
  var months = [ "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December" ];
  var d = new Date(t);
  return months[d.getUTCMonth()] + " " + d.getUTCDate() + ", " + d.getUTCFullYear();
}
exports.albumReleaseDateLong = albumReleaseDateLong;

function appleGlyph() {
  return {
    n: "svg", c: "egt-stream__logo", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false",
    i: { n: "path", fill: "currentColor",
      d: "M17.05 12.54c-.02-2.05 1.68-3.03 1.75-3.08-.95-1.4-2.44-1.59-2.97-1.61-1.26-.13-2.46.74-3.1.74-.64 0-1.63-.72-2.68-.7-1.38.02-2.65.8-3.36 2.04-1.43 2.49-.37 6.17 1.02 8.19.68.99 1.49 2.1 2.56 2.06 1.03-.04 1.42-.66 2.66-.66 1.24 0 1.59.66 2.68.64 1.11-.02 1.81-1 2.49-1.99.78-1.14 1.1-2.25 1.12-2.31-.02-.01-2.15-.83-2.17-3.29zM15.1 6.42c.56-.68.94-1.63.84-2.57-.81.03-1.79.54-2.37 1.21-.52.6-.98 1.56-.86 2.48.9.07 1.83-.46 2.39-1.12z" }
  };
}
function spotifyGlyph() {
  return {
    n: "svg", c: "egt-stream__logo", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false",
    i: { n: "path", fill: "currentColor",
      d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.59 14.43c-.18.3-.57.39-.87.21-2.39-1.46-5.4-1.79-8.94-.98-.34.08-.68-.14-.76-.48-.08-.34.14-.68.48-.76 3.88-.89 7.21-.51 9.89 1.13.3.18.39.57.2.88zm1.22-2.72c-.23.37-.71.49-1.08.26-2.74-1.68-6.91-2.17-10.15-1.18-.42.13-.86-.11-.99-.53-.13-.42.11-.86.53-.99 3.7-1.12 8.29-.58 11.42 1.35.37.23.49.72.27 1.09zm.11-2.83C14.64 8.85 9.4 8.66 6.4 9.57c-.5.15-1.03-.13-1.18-.63-.15-.5.13-1.03.63-1.18 3.45-1.05 9.23-.85 12.86 1.31.45.27.6.85.33 1.3-.27.45-.85.6-1.3.33z" }
  };
}
function youtubeMusicGlyph() {
  return {
    n: "svg", c: "egt-stream__logo", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false",
    i: { n: "path", fill: "currentColor",
      d: "M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228s6.228-2.796 6.228-6.228S15.432 5.772 12 5.772zM9.684 15.54V8.46L15.816 12l-6.132 3.54z" }
  };
}
/* Icon-only pill — the brand glyph is the link; the service name lives in the
 * aria-label for screen readers, never as visible text (founder rule). */
function streamPill(href, label, glyph, mod) {
  return tooltip({
    n: "a", href: href, target: "_blank", rel: "noopener",
    c: "egt-stream egt-stream--" + mod, "aria-label": "Open in " + label,
    title: label,
    i: glyph
  }, "top");
}
/* The youtube pill's accessible name must reflect its actual destination. A
 * YouTube Music link (music.youtube.com) or a playlist (list= present, which is
 * what the released album pages resolve to on YT Music) IS "YouTube Music"; a
 * bare channel/handle/video (e.g. Act 2's interim youtube.com/@egtuniverse main
 * channel) is plain "YouTube" — labelling that "YouTube Music" misleads a screen
 * reader. Glyph is shared (icon-only; the name lives only in aria-label/title). */
function youtubeLabel(url) {
  url = (url || "").toString();
  if (/music\.youtube\.com/i.test(url) || /[?&]list=/i.test(url)) { return "YouTube Music"; }
  return "YouTube";
}
exports.youtubeLabel = youtubeLabel;
/* Shared with the podcast pages (egt.podcasts.views.js): icon-only stream pills. */
exports.streamPill = streamPill;
exports.glyphs = { apple: appleGlyph, spotify: spotifyGlyph, youtube: youtubeMusicGlyph };
exports.streamLinks = function streamLinks(apple, spotify, youtube) {
  var links = [];
  if (apple) { links.push(streamPill(apple, "Apple Music", appleGlyph(), "apple")); }
  if (spotify) { links.push(streamPill(spotify, "Spotify", spotifyGlyph(), "spotify")); }
  if (youtube) { links.push(streamPill(youtube, youtubeLabel(youtube), youtubeMusicGlyph(), "youtube")); }
  return links;
};
exports.track = function track(t) {
  /* The art + title + meta are ONE link to the album's own detail page
   * (/entertainment/<id> — the track-preview page). The streaming pills stay a
   * SEPARATE sibling (never nested inside this anchor — a nested <a> is invalid
   * HTML and would make a pill tap also fire the album-page nav). So: tap the
   * art/title/meta → the album page; tap a pill → the DSP opens (its own target
   * _blank link, untouched). */
  var albumLink = {
    n: "a", href: "/entertainment/" + t.id, c: "egt-trk__link",
    "aria-label": "Open the " + (t.nm || "album") + " album page",
    i: [
      { n: "div", c: "egt-trk__art",
        i: { n: "img", src: t.art, alt: (t.nm || "") + " — album art", loading: "lazy", decoding: "async" } },
      { n: "div", c: "egt-trk__id",
        i: [ { n: "div", c: "egt-trk__nm", i: t.nm }, { n: "div", c: "egt-trk__meta", i: t.meta || t.line } ] }
    ]
  };
  return {
    n: "div", c: "egt-trk",
    i: [
      albumLink,
      { n: "div", c: "egt-trk__lk", i: exports.streamLinks(t.apple, t.spotify, t.youtube) }
    ]
  };
};

/* =====================================================================
 * FOOTNOTES — a server-renderable footnote system in the EGT design language.
 *
 * Why not the platform's ua.footnote/ua.footnotes controls: they are
 * supportServer:false (client-only) and emit raw serverNotSupported JSON on
 * these server-rendered, crawlable EGT pages — unusable here. Their state is
 * also module-global (no per-render reset). This factory is the intended
 * replacement: each call returns a fresh per-render closure (NO shared state
 * across requests), so numbering always starts at 1 and is request-scoped.
 *
 * Usage:
 *   var FN = S.footnotes();
 *   // attach a numbered superscript marker to a title (append to its `i` array):
 *   { n: "h2", i: [ "Do more.", FN.mark("Illustrative — sample data…") ] }
 *   // render the numbered list once, at the bottom of the page:
 *   sections.push(FN.list());   // empty <div> if no marks were made
 *
 * The marker links down to the note; each note links back up to its marker.
 * Styling lives per-surface (.egt-fn-mark / .egt-fn / .egt-fn__list), gold link
 * + muted neutral text on true-black — see app/scss/egt.platform.scss.
 * ===================================================================== */
exports.footnotes = function footnotes() {
  var notes = [];
  /* build a marker node for note number `n`. Only the FIRST reference to a note
   * carries id="egt-fn-ref-N" (the back-link target); deduped repeats omit the
   * id so the DOM never holds a duplicate id and the ↩ resolves to the first
   * reference. */
  function marker(n, first) {
    var a = { n: "a", c: "ua-a-incenter", href: "#egt-fn-" + n,
      "aria-label": "Footnote " + n, i: String(n) };
    /* ua-a-incenter is now only a legacy MARKER: as of 2026-07-24 the platform's
     * scroll-to-centre mechanism (uat/src/elements/ua.a.incenter.js) matches
     * EVERY same-page hash link, not just the ones carrying this class, because
     * the founder asked for centring site-wide. Keeping the class costs nothing
     * and keeps these markers readable as "this is a jump", but do NOT rely on
     * it to opt a new link IN — every `#` link is already in. To opt one OUT,
     * use `data-ua-incenter="off"`. */
    if (first) { a.id = "egt-fn-ref-" + n; }
    return { n: "sup", c: "egt-fn-mark", i: a };
  }
  return {
    /* returns a numbered superscript marker linking to the note; call inline in
     * a title. DEDUPES by exact text: identical note text reuses the SAME number
     * (the note is listed once) instead of pushing a duplicate. A first
     * occurrence numbers normally (backward-compatible with prior callers). */
    mark: function mark(text) {
      var idx = notes.indexOf(text);
      if (idx !== -1) { return marker(idx + 1, false); }
      notes.push(text);
      return marker(notes.length, true);
    },
    /* returns the numbered list section for the bottom of the page; an empty
     * div if no marks were made (so it can be added unconditionally). */
    list: function list() {
      if (!notes.length) { return { n: "div" }; }
      return {
        n: "section", c: "egt-section egt-fn",
        i: { n: "div", c: "egt__shell",
          i: [
            { n: "p", c: "egt__eyebrow", i: "Footnotes" },
            { n: "ol", c: "egt-fn__list",
              i: notes.map(function (t, i) {
                return { n: "li", c: "egt-fn__item", id: "egt-fn-" + (i + 1),
                  i: [ t, " ", { n: "a", c: "egt-fn__back ua-a-incenter", href: "#egt-fn-ref-" + (i + 1),
                    "aria-label": "Back to reference " + (i + 1), i: "↩" } ] };
              }) }
          ] }
      };
    }
  };
};

exports.ROOMS = ROOMS;
exports.PALETTES = PALETTES;

/* ===================================================================
 * THE PRICE TOKEN — "$75 + shipping", which must NEVER break across lines.
 *
 * A line ending on a bare "$75", or on "$75 +", advertises a price that is not
 * the price. That is the exact defect the token exists to prevent, so wrapping
 * it is not a cosmetic concern — a mid-token break re-introduces the bug in the
 * one place a buyer is most likely to read it.
 *
 * ONE helper, ONE class, every site that ADVERTISES the figure: the CTA labels,
 * the gold `.egt-exp__price` spans, the `micro--label` eyebrow, the body prose,
 * the FAQ answer and the wizard's info cards. Per-site CSS was tried and failed
 * — it covered the CTA and left six other sites splitting.
 *
 * `priceToken()` is mirrored byte-for-byte in app/public/src/exploit.reserve.js
 * for the client-injected wizard copy, which never passes through this module.
 * Both paths must emit identical markup; change them together or not at all.
 *
 * NOTE: this is for ADVERTISING a single figure only. Never run itemized copy
 * through it — the checkout breaks "Books $75.00" / "Shipping $7.99" / "Total
 * today" out on purpose, and those lines are computed from the real charge.
 * ================================================================= */
var PRICE_TOKEN = "$75 + shipping";

/* Wrap the token wherever it appears — a string, an array, or a node's `i`
 * children — so protection is STRUCTURAL, not author-remembered. A string
 * splits into [text, <span.egt-price-token>, text]; an array or a node tree is
 * walked RECURSIVELY, so an array-form paragraph (the §04b / §19 shape) guards
 * its tokens automatically instead of relying on a hand-typed class.
 *
 * Idempotent: a node already carrying `egt-price-token` is returned untouched,
 * so re-guarding an already-guarded tree is a no-op. Non-token strings, and any
 * array/node containing no token, are returned BY IDENTITY (no rebuild) — the
 * valid page therefore renders byte-identical; this only adds detection of the
 * invalid case, it never changes correct output. */
function priceToken(node) {
  if (typeof node === "string") {
    if (node.indexOf(PRICE_TOKEN) < 0) { return node; }
    var out = [], rest = node, at;
    while ((at = rest.indexOf(PRICE_TOKEN)) >= 0) {
      if (at > 0) { out.push(rest.slice(0, at)); }
      out.push({ n: "span", c: "egt-price-token", i: PRICE_TOKEN });
      rest = rest.slice(at + PRICE_TOKEN.length);
    }
    if (rest) { out.push(rest); }
    return out;
  }
  if (Array.isArray(node)) {
    var changed = false, flat = [];
    for (var k = 0; k < node.length; k++) {
      var r = priceToken(node[k]);
      if (r !== node[k]) { changed = true; }
      if (Array.isArray(r)) { for (var j = 0; j < r.length; j++) { flat.push(r[j]); } }
      else { flat.push(r); }
    }
    return changed ? flat : node;
  }
  if (node && typeof node === "object" && node.n) {
    if (typeof node.c === "string" && node.c.indexOf("egt-price-token") >= 0) { return node; }
    if (!("i" in node)) { return node; }
    var gi = priceToken(node.i);
    if (gi === node.i) { return node; }
    var copy = {};
    for (var key in node) { if (Object.prototype.hasOwnProperty.call(node, key)) { copy[key] = node[key]; } }
    copy.i = gi;
    return copy;
  }
  return node;
}

/* The LOUD half of the guard — a build/render-time integrity check. Walks a
 * rendered node tree and THROWS if any VISIBLE occurrence of the token is not
 * inside an `egt-price-token`-classed element. This catches the failure the
 * recursion above cannot: a paragraph built as a raw node object that bypasses
 * priceToken() entirely (the §04b / §19 shape) and forgets — or mis-types — the
 * class. A missing/mis-typed guard becomes a hard error at render, never a price
 * that silently splits mid-line in front of a buyer.
 *
 * `<script>` nodes (JSON-LD) are skipped: the FAQ answer is single-sourced into
 * the FAQPage graph as PLAIN TEXT on purpose (schema.org requires it), so the
 * token legitimately appears there unwrapped. Only element children (`i`) are
 * inspected, never attributes. Read-only — returns the tree unchanged. */
function assertPriceTokensGuarded(tree) {
  walk(tree, false);
  return tree;
  function walk(node, guarded) {
    if (node == null) { return; }
    if (typeof node === "string") {
      if (!guarded && node.indexOf(PRICE_TOKEN) >= 0) {
        throw new Error("egt price-token guard: unguarded \"" + PRICE_TOKEN +
          "\" found in visible copy. Route the copy through S.priceToken() or " +
          "wrap it in an element carrying the egt-price-token class. Offending text: " +
          JSON.stringify(node.length > 80 ? node.slice(0, 80) + "…" : node));
      }
      return;
    }
    if (Array.isArray(node)) {
      for (var i = 0; i < node.length; i++) { walk(node[i], guarded); }
      return;
    }
    if (typeof node === "object" && node.n) {
      if (node.n === "script") { return; }
      var g = guarded || (typeof node.c === "string" && node.c.indexOf("egt-price-token") >= 0);
      walk(node.i, g);
    }
  }
}

exports.PRICE_TOKEN = PRICE_TOKEN;
exports.priceToken = priceToken;
exports.assertPriceTokensGuarded = assertPriceTokensGuarded;


/* THE EGT* BRAND MARK (founder ruling 2026-10-05): "EGT" in white Playfair and
 * the asterisk in gold Playfair, ALWAYS together on one line (never wraps the
 * star onto its own line). Styled globally by .egt-mark in _egt.scss. The
 * accessible name is "EGT" (the star is decorative). */
function egtMark() {
  return { n: "span", c: "egt-mark",
    i: [ "EGT", { n: "span", c: "egt-mark__star", "aria-hidden": "true", i: "*" } ] };
}
exports.egtMark = egtMark;

/* Plain copy → nodes: every "EGT*" in a string becomes the brand mark. */
exports.brand = function brand(str) {
  var parts = String(str).split("EGT*"), out = [];
  parts.forEach(function (p, i) { if (i) { out.push(egtMark()); } if (p) { out.push(p); } });
  return out;
};
